import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import { Modal, ModalContent, ModalFooter } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import { Form, Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import * as brandApi from 'apis/restaurant-api/merchant-admin/brand/api';
import * as enterpriseApi from 'apis/restaurant-api/merchant-admin/enterprise/api';
import * as merchantApi from 'apis/restaurant-api/merchant-admin/merchant/api';
import * as venueApi from 'apis/restaurant-api/merchant-admin/venue/api';
import { APIErrorNotification, RFFFieldErrorWrapper } from 'common/components';
import { useRetailContext } from 'common/hooks';
import validateYupSchema from 'common/utils/validateYupSchema';
import { getAvailableLanguages } from 'locale';
import { UiLanguageKey } from 'locale/languages';

import RoleDescription from './RoleDescription';
import { getUserRoleOptions } from './getUserRoleOptions';
import type { EntityType, Role } from './types';
import { isBrandRole, isEnterpriseRole, isMerchantRole, isVenueRole } from './utils';

type Props = {
  isOpen: boolean;
  entityId: string;
  entityType: EntityType;
  onClose: () => void;
  onSuccess: () => void;
};

type FormData = {
  email: string;
  role: Role | '';
  preferred_locale: UiLanguageKey;
};

const availableLanguages = getAvailableLanguages();
const availableLanguageKeys = availableLanguages.map(({ key }) => key);

const validationSchema = yup.object({
  email: yup.string().email().required(),
  role: yup.mixed<Role>().required(),
  preferred_locale: yup.mixed<UiLanguageKey>().oneOf(availableLanguageKeys).required(),
});

const buildInitialValues = (): FormData => {
  const [firstLanguage] = availableLanguages;
  return {
    email: '',
    role: '',
    preferred_locale: firstLanguage.key,
  };
};

const StyledFormRow = styled(FormRow).attrs({
  labelAlign: 'center',
  horizontalPadding: true,
})``;

const CloseButton = styled(Button).attrs({
  variant: 'secondary',
  size: 'small',
})``;

const SubmitButton = styled(Button).attrs({
  variant: 'primary',
  size: 'small',
})``;

const SubmitError = styled(APIErrorNotification)``;

const StyledModalFooter = styled(ModalFooter)`
  flex-shrink: 0;
  display: block;
  overflow: auto;
`;

const FooterButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const formId = 'user-invite-form';

const InviteModal = ({ isOpen, onClose, onSuccess, entityType, entityId }: Props) => {
  const intl = useIntl();
  const isRetailContext = useRetailContext();

  const grantAccessMutation = useMutation(
    async ({
      entityId,
      entityType,
      formData: { email, role, preferred_locale },
    }: {
      entityId: string;
      entityType: EntityType;
      formData: yup.InferType<typeof validationSchema>;
    }) => {
      if (entityType === 'merchant' && isMerchantRole(role)) {
        return merchantApi.grantTentativeMerchantAccess(entityId, email, role, preferred_locale);
      }

      if (entityType === 'venue' && isVenueRole(role)) {
        return venueApi.grantTentativeVenueAccess(entityId, email, role, preferred_locale);
      }

      if (entityType === 'enterprise' && isEnterpriseRole(role)) {
        return enterpriseApi.grantTentativeEnterpriseAccess(entityId, email, role, preferred_locale);
      }

      if (entityType === 'brand' && isBrandRole(role)) {
        return brandApi.grantTentativeBrandAccess(entityId, email, role, preferred_locale);
      }

      return null;
    },
    {
      onSuccess,
    },
  );

  const handleFormSubmit = async (formData: FormData) => {
    try {
      const validFormData = await validationSchema.validate(formData);
      await grantAccessMutation.mutateAsync({
        entityId,
        entityType,
        formData: validFormData,
      });
    } catch (error) {
      return {
        [FORM_ERROR]: error,
      };
    }
    return undefined;
  };

  const roleOptions = getUserRoleOptions(entityType, isRetailContext, intl);

  return (
    <Modal
      open={isOpen}
      onDismiss={onClose}
      sizePreset="centered:medium"
      title={intl.formatMessage({ id: 'userManagement.inviteHeader' })}
      ContentComponent={null}
    >
      <Form
        onSubmit={handleFormSubmit}
        initialValues={buildInitialValues()}
        keepDirtyOnReinitialize
        validate={validateYupSchema(validationSchema)}
      >
        {({ handleSubmit, submitting, submitError }) => (
          <>
            <ModalContent disablePadding>
              <form onSubmit={handleSubmit} id={formId}>
                <StyledFormRow label={intl.formatMessage({ id: 'userManagement.email' })}>
                  <Field<FormData['email']> name="email" parse={v => v}>
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input {...input} autoFocus />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </StyledFormRow>
                <StyledFormRow label={intl.formatMessage({ id: 'userManagement.selectRights' })}>
                  <Field<FormData['role']> name="role">
                    {({ input }) => (
                      <Select
                        {...input}
                        placeholder={intl.formatMessage({ id: 'userManagement.selectRolePrompt' })}
                        options={roleOptions}
                        menuPortalTarget={document.body}
                      />
                    )}
                  </Field>
                </StyledFormRow>
                <StyledFormRow label={intl.formatMessage({ id: 'userManagement.preferredLanguage' })}>
                  <Field<FormData['preferred_locale']> name="preferred_locale">
                    {({ input }) => (
                      <Select
                        {...input}
                        options={getAvailableLanguages().map(({ label, key: value }) => ({
                          label,
                          value,
                        }))}
                        menuPlacement="top"
                        maxMenuHeight={200}
                        menuPortalTarget={document.body}
                      />
                    )}
                  </Field>
                </StyledFormRow>
                <Field<FormData['role']> name="role">
                  {({ input: { value: role } }) => (role !== '' ? <RoleDescription role={role} /> : null)}
                </Field>
              </form>
            </ModalContent>

            <StyledModalFooter>
              {submitError && <SubmitError error={submitError} style={{ marginBottom: 16 }} />}

              <FooterButtonsContainer>
                <CloseButton onClick={onClose} disabled={submitting}>
                  <FormattedMessage id="userManagement.close" />
                </CloseButton>

                <SubmitButton type="submit" disabled={submitting} loading={submitting} form={formId}>
                  <FormattedMessage id="userManagement.sendInvitation" />
                </SubmitButton>
              </FooterButtonsContainer>
            </StyledModalFooter>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default InviteModal;
